import { useEffect, useRef, useState, useCallback } from 'react';

function isIOSDevice() {
  return (
    /iPad|iPhone|iPod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

function pickMimeType(preferMp4) {
  const candidates = preferMp4
    ? ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg']
    : ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];

  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) || '';
}

export function formatRecordingDuration(totalSeconds) {
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

export function useInterviewRecorder(enabled) {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingError, setRecordingError] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const mimeTypeRef = useRef('');
  const iosRef = useRef(isIOSDevice());

  const cleanupStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const startRecording = useCallback(async () => {
    if (!enabled || !navigator.mediaDevices?.getUserMedia) {
      if (enabled) {
        setRecordingError('Gravação de áudio não suportada neste navegador.');
      }
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
      streamRef.current = stream;
      chunksRef.current = [];

      const mimeType = pickMimeType(iosRef.current);
      mimeTypeRef.current = mimeType;

      const options = mimeType
        ? iosRef.current
          ? { mimeType }
          : { mimeType, audioBitsPerSecond: 32000 }
        : iosRef.current
          ? {}
          : { audioBitsPerSecond: 32000 };

      const recorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      // iOS Safari entrega os chunks principalmente no stop — sem timeslice
      if (iosRef.current) {
        recorder.start();
      } else {
        recorder.start(1000);
      }

      startTimeRef.current = Date.now();
      setElapsedSeconds(0);
      setIsRecording(true);
      setRecordingError(null);

      timerRef.current = setInterval(() => {
        if (startTimeRef.current) {
          setElapsedSeconds(Math.floor((Date.now() - startTimeRef.current) / 1000));
        }
      }, 1000);
    } catch (err) {
      console.error('Erro ao iniciar gravação:', err);
      cleanupStream();
      setRecordingError('Não foi possível acessar o microfone. A entrevista será salva sem áudio.');
    }
  }, [enabled, cleanupStream]);

  const stopRecording = useCallback(() => {
    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;
      clearInterval(timerRef.current);

      const resolveResult = (blob, mimeType) => {
        const duration = startTimeRef.current
          ? Math.floor((Date.now() - startTimeRef.current) / 1000)
          : elapsedSeconds;

        cleanupStream();
        mediaRecorderRef.current = null;
        setIsRecording(false);
        resolve({ blob, duration, mimeType });
      };

      if (!recorder || recorder.state === 'inactive') {
        const duration = startTimeRef.current
          ? Math.floor((Date.now() - startTimeRef.current) / 1000)
          : elapsedSeconds;
        cleanupStream();
        resolve({ blob: null, duration, mimeType: mimeTypeRef.current || null });
        return;
      }

      recorder.onstop = () => {
        const rawMime = recorder.mimeType || mimeTypeRef.current || 'audio/webm';
        const mimeType = rawMime.split(';')[0].trim() || 'audio/webm';
        const validChunks = chunksRef.current.filter((chunk) => chunk && chunk.size > 0);
        const blob = validChunks.length > 0 ? new Blob(validChunks, { type: mimeType }) : null;
        resolveResult(blob, mimeType);
      };

      if (typeof recorder.requestData === 'function') {
        recorder.requestData();
      }

      if (iosRef.current) {
        setTimeout(() => {
          if (recorder.state !== 'inactive') {
            recorder.stop();
          }
        }, 200);
      } else {
        recorder.stop();
      }
    });
  }, [cleanupStream, elapsedSeconds]);

  useEffect(() => {
    if (enabled) {
      startRecording();
    }

    return () => {
      clearInterval(timerRef.current);
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state === 'recording') {
        recorder.stop();
      }
      cleanupStream();
    };
  }, [enabled, startRecording, cleanupStream]);

  return { isRecording, recordingError, elapsedSeconds, stopRecording };
}
